import Vue from 'vue';
import { mapGetters } from 'vuex';
import * as d3 from 'd3';
import download from 'downloadjs';
import html2canvas from 'html2canvas';
import PptxGenJS from 'pptxgenjs';
import BotAPI from 'src/api/chatbots';
import DrawUtils from 'src/utils/draw';
import Utils from 'src/utils/general';
import { BfSpinner, BfButton, BfTextInput } from 'components/Butterfly';
import { Form, Field, } from 'components/forms';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import { elementToSVG } from 'dom-to-svg';
import icon from 'assets/img/download.svg';
import slackIcon from 'assets/img/slack.svg';
import microsoftIcon from 'assets/img/microsoft-teams.svg';
import Icon from 'components/Icon.vue';
import Modal from 'src/components/project/analysis/results/Modal.vue';
import AlertsModal from 'src/components/AlertsModal.vue';
// While the teams message limit says in this documentation that the limit is
// 28kB, through experimentation I have found that it can fail intermittently
// above 21kB
// https://learn.microsoft.com/en-us/microsoftteams/limits-specifications-teams
const TEAMS_MESSAGE_LIMIT = 21000;
const DownloadExportButton = Vue.extend({
    components: {
        Dropdown,
        DropdownItem,
        BfSpinner,
        BfButton,
        BfTextInput,
        Form,
        Field,
        Icon,
        Modal,
        AlertsModal,
    },
    props: {
        /*
        * name: String - A string representation of the title
        * shortName: Name of the export only (used for analytics)
        * getEl: A vanillaJs selector of the SVG element to be exported
        * getCsvData:
        * getSvgExportConfig: {
        *   dims: The dimensions of the svg
        *   css: The overwrite styles applied to the export as a string
        *   }
        */
        name: { type: String, default: '' },
        shortName: { type: String, default: '' },
        isLoading: { type: Boolean, default: false },
        getEl: { type: Function, default: null },
        getCsvData: { type: Function, default: null },
        getSvgExportConfig: { type: Function, default: null },
        botShareEnabled: { type: Boolean, default: true },
        showAlerts: { type: Boolean, default: false },
        makePptSlide: { type: Function, default: null },
        basicSvgExport: { type: Boolean, default: false },
    },
    data() {
        return {
            icon,
            slackIcon,
            microsoftIcon,
            slackShareConfig: {
                dialogVisible: false,
                channel: '',
                message: '',
                error: null,
            },
            microsoftTeamsShareConfig: {
                dialogVisible: false,
                channel: '',
                message: '',
                error: null,
            },
            loading: false,
            slackChannels: [],
            teamsChannels: [],
        };
    },
    computed: {
        ...mapGetters([
            'featureFlags',
            'currentSite',
        ]),
        supportsClipboard() {
            return typeof ClipboardItem !== 'undefined';
        },
        // Detect IE because our PNG export method is not compatible with pre-Edge Microsoft.
        isIE() {
            return Utils.isIE();
        },
        initialFormValues() {
            return {
                channel: null,
                message: '',
            };
        },
        integrations() {
            return {
                slack: this.currentSite?.chatbot_integrations?.slack,
                teams: this.currentSite?.chatbot_integrations?.teams,
            };
        },
    },
    methods: {
        async setDialogVisible(shareObjName) {
            this[shareObjName].dialogVisible = true;
            if (shareObjName === 'slackShareConfig') {
                this.getSlackChannels();
            }
            else if (shareObjName === 'microsoftTeamsShareConfig') {
                this.getTeamsChannels();
            }
        },
        resetShare(shareObjName) {
            this[shareObjName] = {
                dialogVisible: false,
                channel: '',
                message: '',
                error: null,
            };
        },
        scaleToMinimum(width, height, min) {
            const scale = Math.max(min / width, min / height);
            return Math.max(1, scale);
        },
        exportData(functionName, ...args) {
            this[functionName]?.(...args);
        },
        serializeSvg(el, exportConfig, scale = 1.5) {
            const svgDocument = elementToSVG(el);
            // activeElement for Firefox, firstChild for Chrome
            const svg = svgDocument.activeElement ?? svgDocument.firstChild;
            if (svg === null) {
                throw new Error("Unexpected missing svg element");
            }
            // Rescale.
            const currentWidth = parseFloat(svg.getAttribute('width'));
            const currentHeight = parseFloat(svg.getAttribute('height'));
            svg.setAttribute('width', `${currentWidth * scale}`);
            svg.setAttribute('height', `${currentHeight * scale}`);
            // Create a <style> element and add it to <defs> if it doesn't exist.
            const svgDefs = svg.querySelector('defs') || svgDocument.createElementNS("http://www.w3.org/2000/svg", "defs");
            const svgStyle = svgDocument.createElementNS("http://www.w3.org/2000/svg", "style");
            let dims = exportConfig.dims;
            let css = exportConfig.css || '';
            css += `
        @font-face {
          font-family: Lato;
          src: local('Lato'), url(data:application/font-ttf;base64,${DrawUtils.getLatoFontBase64()}) format('truetype');
        }
        text {
          font-family: Lato;
        }
      `;
            svgStyle.textContent = `
        <defs>
              <style>/* <![CDATA[ */ ${css} /* ]]> */</style>
        </defs>
      `;
            svgDefs.appendChild(svgStyle);
            svg.appendChild(svgDefs);
            const svgContent = new XMLSerializer().serializeToString(svgDocument);
            return encodeURIComponent(svgContent);
        },
        serializeSvgBasic(el, exportConfig, scale = 1) {
            let dims = exportConfig.dims;
            let css = exportConfig.css || '';
            css += `
        @font-face {
          font-family: Lato;
          src: local('Lato'), url(data:application/font-ttf;base64,${DrawUtils.getLatoFontBase64()}) format('truetype');
        }
        text {
          font-family: Lato;
        }
      `;
            let serializer = new XMLSerializer();
            let svgContent = `<svg width="${dims.width * scale}" height="${dims.height * scale}"
              viewBox="0 0 ${dims.width} ${dims.height}" style="background:white"
              version="1.1" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <style>/* <![CDATA[ */ ${css} /* ]]> */</style>
              </defs>
              ${Array.from(el.children).map((c) => serializer.serializeToString(c)).join('')}
        </svg>`;
            return encodeURIComponent(svgContent);
        },
        downloadCsv() {
            let csvData = d3.csvFormat(this.getCsvData());
            Utils.downloadCsv(csvData, this.name);
            this.$analytics.track.analysis.downloadExport(this.shortName, 'CSV', { fullName: this.name });
        },
        async downloadPPT() {
            const pptx = new PptxGenJS();
            await this.makePptSlide?.(pptx);
            pptx.writeFile({ fileName: `${this.name}.pptx` });
            this.$analytics.track.analysis.downloadExport(this.shortName, 'PPT', { fullName: this.name });
        },
        async exportImage(method, values) {
            const maxBytes = method === 'microsoftTeams' ?
                (TEAMS_MESSAGE_LIMIT - this.microsoftTeamsShareConfig.message.length) :
                undefined;
            const imageType = method === 'microsoftTeams' ? 'image/webp' : 'image/png';
            const imageData = await this.generateImageData(imageType, maxBytes);
            switch (method) {
                case 'slack':
                    this.shareToSlack(imageData, values);
                    break;
                case 'microsoftTeams':
                    this.shareToMicrosoftTeams(imageData, values);
                    break;
                case 'download':
                    download(imageData, `${this.name}.png`, 'image/png');
                    break;
                case 'clipboard':
                    // Convery the data url to a blob and copy to clipboard
                    fetch(imageData)
                        .then((res) => res.blob())
                        .then((blob) => {
                        navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': blob
                            })
                        ]);
                    });
                    break;
            }
            this.$analytics.track.analysis.downloadExport(this.shortName, 'PNG', { fullName: this.name, exportMethod: method });
        },
        async shareToSlack(image, values) {
            this.loading = true;
            this.slackShareConfig.error = null;
            if (!values.channel) {
                return;
            }
            try {
                await BotAPI.shareToSlack(image, values.channel, values.message);
                this.resetShare('slackShareConfig');
            }
            catch (e) {
                if (e.status === 500) {
                    this.slackShareConfig.error = 'There was an error sharing to Slack. Please try again.';
                }
                else {
                    this.slackShareConfig.error = e.bodyText;
                }
            }
            finally {
                this.loading = false;
            }
        },
        async shareToMicrosoftTeams(image, values) {
            this.loading = true;
            this.microsoftTeamsShareConfig.error = null;
            if (!values.channel) {
                return;
            }
            try {
                await BotAPI.shareToTeams(image, values.channel, values.message);
                this.resetShare('microsoftTeamsShareConfig');
            }
            catch (e) {
                if (e.status === 500) {
                    this.microsoftTeamsShareConfig.error = 'There was an error sharing to Teams. Please try again.';
                }
                else {
                    this.microsoftTeamsShareConfig.error = e.bodyText;
                }
            }
            finally {
                this.loading = false;
            }
        },
        async generateImageData(imageType, maxBytes = undefined) {
            let el = await this.getEl();
            let tagName = el?.tagName.toLowerCase();
            let promise;
            if (tagName === 'svg') {
                promise = this.svgToDataUrl(imageType, maxBytes);
            }
            else if (tagName === 'canvas') {
                promise = this.canvasToDataUrl(imageType, maxBytes);
            }
            else if (tagName === 'div' || tagName === 'span') {
                promise = this.elToDataUrl(el, imageType, maxBytes);
            }
            return promise;
        },
        svgToDataUrl(imageType = 'image/png', maxBytes = undefined, viewableOnly = false) {
            return new Promise(async (resolve) => {
                let exportConfig = this.getSvgExportConfig(viewableOnly);
                let dims = exportConfig.dims;
                let padding = exportConfig.padding || 0;
                let scale = 1;
                if (maxBytes === undefined) {
                    scale = this.scaleToMinimum(dims.width, dims.height, 1400);
                }
                // Use canvas and image to convert svg to png and download it
                let container = document.createElement('div');
                container.style.display = 'none';
                document.body.appendChild(container);
                let canvas = document.createElement('canvas');
                canvas.style.display = 'none';
                container.appendChild(canvas);
                let context = canvas.getContext('2d');
                let img = new Image();
                img.width = canvas.width = dims.width * scale;
                img.height = canvas.height = dims.height * scale;
                img.addEventListener('load', () => {
                    // This setTimeout fixes a WebKit bug causing invisible text
                    // due to the image being rendered before the @font-face has loaded
                    setTimeout(() => {
                        if (context === null)
                            return;
                        context.fillStyle = '#fff';
                        context.fillRect(0, 0, canvas.width, canvas.height);
                        context.drawImage(img, padding, padding, canvas.width - padding * 2, canvas.height - padding * 2);
                        const dataUrl = canvas.toDataURL(imageType);
                        if (maxBytes && dataUrl.length > maxBytes) {
                            scale = maxBytes / dataUrl.length;
                            img.width = canvas.width = dims.width * scale;
                            img.height = canvas.height = dims.height * scale;
                            context.fillRect(0, 0, canvas.width, canvas.height);
                            context.drawImage(img, padding, padding, canvas.width - padding * 2, canvas.height - padding * 2);
                        }
                        resolve(canvas.toDataURL(imageType));
                        // Cleanup
                        container.remove();
                    }, 0);
                });
                const el = await this.getEl();
                const serialize = this.basicSvgExport ? this.serializeSvgBasic : this.serializeSvg;
                img.src = 'data:image/svg+xml; charset=utf8,' + serialize(el, exportConfig, scale);
                return img;
            });
        },
        canvasToDataUrl(imageType = 'image/png', maxBytes = undefined) {
            return new Promise(async (resolve) => {
                let el = await this.getEl();
                // Temporarily fill canvas with white background and copy image data for export
                let canvas = el;
                let context = canvas.getContext('2d');
                let w = canvas.width;
                let h = canvas.height;
                let data = context.getImageData(0, 0, w, h);
                let compositeOperation = context.globalCompositeOperation;
                context.globalCompositeOperation = 'destination-over';
                context.fillStyle = 'white';
                context.fillRect(0, 0, w, h);
                context.clearRect(0, 0, w, h);
                context.putImageData(data, 0, 0);
                const dataUrl = canvas.toDataURL(imageType);
                if (maxBytes && dataUrl.length > maxBytes) {
                    const scale = maxBytes / dataUrl.length;
                    canvas.width = canvas.width * scale;
                    canvas.height = canvas.height * scale;
                }
                context.globalCompositeOperation = compositeOperation;
                resolve(canvas.toDataURL(imageType));
            });
        },
        elToDataUrl(el, imageType = 'image/png', maxBytes = undefined) {
            return new Promise(async (resolve) => {
                // If it's just a html image, we're going to use domtoimage
                const isIE11 = (!(window.ActiveXObject) && 'ActiveXObject' in window);
                const options = {
                    logging: process.env.APP_ENV !== 'production',
                    removeContainer: true,
                    scrollX: 0,
                    scrollY: isIE11 ? -window.pageYOffset : -window.scrollY,
                };
                html2canvas(el, options)
                    .then(canvasel => {
                    const dataUrl = canvasel.toDataURL(imageType);
                    if (maxBytes && dataUrl.length > maxBytes) {
                        const scale = maxBytes / dataUrl.length;
                        canvasel.width = canvasel.width * scale;
                        canvasel.height = canvasel.height * scale;
                    }
                    resolve(canvasel.toDataURL(imageType));
                });
            });
        },
        downloadSvg() {
            let exportConfig = this.getSvgExportConfig();
            const serialize = this.basicSvgExport ? this.serializeSvgBasic : this.serializeSvg;
            download('data:image/svg+xml; charset=utf8,' + serialize(this.getEl(), exportConfig), `${this.name}.svg`, 'image/svg+xml');
            this.$analytics.track.analysis.downloadExport(this.shortName, 'SVG', { fullName: this.name });
        },
        async getSlackChannels() {
            try {
                const channels = await BotAPI.getSlackChannels();
                this.slackChannels = channels;
            }
            catch (e) {
                console.error(e);
                this.slackShareConfig.error = 'Failed to fetch Slack channels.';
            }
        },
        async getTeamsChannels() {
            try {
                const channels = await BotAPI.listWebhooks('teams');
                this.teamsChannels = channels;
            }
            catch (e) {
                console.error(e);
                this.microsoftTeamsShareConfig.error = 'Failed to fetch Teams channels.';
            }
        },
        teamsDetailsToDisplayName(details) {
            return `${details?.team?.displayName} - ${details?.channel?.displayName}`;
        },
        createAlert() {
            this.$refs.alertsModal.open();
        },
        manageAlerts() {
            this.$router.push({ name: 'alerts' });
        },
    }
});
export default DownloadExportButton;
